import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Select, MenuItem, FormControl,
  InputLabel, Slider, Chip, Alert, CircularProgress, IconButton, Tooltip,
  Card, CardContent, CardActions, Collapse, Divider, LinearProgress,
  Tabs, Tab, Stack, List, ListItem, ListItemText, ListItemIcon,
  Accordion, AccordionSummary, AccordionDetails, Snackbar, Container,
  ToggleButton, ToggleButtonGroup, Badge, Skeleton, Fade
} from '@mui/material';
import {
  Search as SearchIcon, Clear as ClearIcon, FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Download as DownloadIcon, Share as ShareIcon, Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon, Error as ErrorIcon, Warning as WarningIcon,
  Info as InfoIcon, Link as LinkIcon, PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon, Stop as StopIcon, Check as CheckIcon,
  Close as CloseIcon, ContentCopy as CopyIcon, Bookmark as BookmarkIcon
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import { apiClient } from '../utils/api';
import { createCasesFromResults } from '../services/caseApi';
import ICSRDisplay from './ICSRDisplay';
import CaseManagement from './CaseManagement';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
}));

// Remove StatusChip styled component - just use regular Chip

// Types
interface Article {
  title: string;
  url: string;
  authors_info?: string;
  snippet: string;
  pdf_url?: string;
  doi?: string;
  venue?: string;
  year?: number;
  citationCount?: number;
  source?: string;
  analysis?: {
    abstract: string;
    tags: string[];
    score3: number;
  };
  pharmacovigilanceData?: any;
  status?: 'pending' | 'analyzing' | 'done' | 'error';
}

export const LiteratureReviewEnhanced: React.FC = () => {
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // State
  const [searchMode, setSearchMode] = useState<'keyword' | 'paste' | 'lookup'>('keyword');
  const [searchSource, setSearchSource] = useState<'semantic_scholar' | 'pubmed' | 'both'>('both');
  const [query, setQuery] = useState('');
  const [maxResults, setMaxResults] = useState(10);
  const [minScore, setMinScore] = useState(2);
  const [useBoolean, setUseBoolean] = useState(true);
  
  const [articles, setArticles] = useState<Article[]>([]);
  const [analyzedArticles, setAnalyzedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [progress, setProgress] = useState(0);
  
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set());
  const [activityLog, setActivityLog] = useState<Array<{time: string; message: string; type: string}>>([]);
  
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info'}>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const [showFiltered, setShowFiltered] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // Main tabs within Literature module: 0 = Search, 1 = Case Management
  const [activeMainTab, setActiveMainTab] = useState<number>(0);

  // Activity Logger
  const addLog = useCallback((message: string, type: 'info' | 'warn' | 'error' | 'success' = 'info') => {
    const time = new Date().toLocaleTimeString();
    setActivityLog(prev => [{time, message, type}, ...prev].slice(0, 50));
  }, []);

  // Map flat extractedFields to ICSRDisplay nested shape (reusable)
  const mapExtractedToICSR = useCallback((ef: any) => {
    if (!ef) return undefined as any;
    return {
      patient: {
        identifier: ef.patient_identifier,
        age: ef.patient_age,
        sex: ef.patient_sex,
        weight: ef.patient_weight,
        height: ef.patient_height,
        pregnancy_status: ef.pregnancy_status,
        medical_history: ef.medical_history,
      },
      adverse_event: {
        event_verbatim: ef.event_verbatim,
        meddra_pt: ef.meddra_pt,
        start_date: ef.ae_start_date,
        stop_date: ef.ae_stop_date,
        duration: ef.ae_duration,
        outcome: ef.outcome,
        seriousness: ef.seriousness,
        severity: ef.severity,
        causality: ef.causality,
      },
      product: {
        name: ef.suspect_product_name,
        generic: ef.suspect_product_generic,
        dosage_form: ef.dosage_form,
        strength: ef.strength,
        indication: ef.indication,
        route: ef.route,
        dose: ef.dose,
        start_date: ef.product_start_date,
        stop_date: ef.product_stop_date,
        batch_number: ef.batch_number,
        action_taken: ef.action_taken,
      },
      reporter: {
        name: ef.reporter_name,
        type: ef.reporter_type,
        occupation: ef.reporter_occupation,
        country: ef.reporter_country,
        relationship: ef.reporter_relationship,
      },
      confidence_score: typeof ef.extraction_confidence === 'number' ? (ef.extraction_confidence / 100) : undefined,
    } as any;
  }, []);

  // ---------- Persistence: save/restore state to survive navigation ----------
  useEffect(() => {
    try {
      const saved = localStorage.getItem('literature_review_enhanced_state');
      if (saved) {
        const st = JSON.parse(saved);
        if (st) {
          setSearchMode(st.searchMode ?? 'keyword');
          setSearchSource(st.searchSource ?? 'both');
          setQuery(st.query ?? '');
          setMaxResults(st.maxResults ?? 10);
          setMinScore(st.minScore ?? 2);
          setUseBoolean(!!st.useBoolean);
          setArticles(Array.isArray(st.articles) ? st.articles : []);
          setAnalyzedArticles(Array.isArray(st.analyzedArticles) ? st.analyzedArticles : []);
          setExpandedCards(new Set<string>(Array.isArray(st.expandedCards) ? st.expandedCards : []));
          setSelectedArticles(new Set<string>(Array.isArray(st.selectedArticles) ? st.selectedArticles : []));
          setActivityLog(Array.isArray(st.activityLog) ? st.activityLog : []);
          setProgress(st.progress ?? 0);
          setActiveMainTab(st.activeMainTab ?? 0);
          // Do not restore loading/analyzing to avoid stale spinners after navigation
          setLoading(false);
          setAnalyzing(false);
          setCurrentStep('');
          // Resume pending analysis if there are unprocessed articles
          const total = (Array.isArray(st.articles) ? st.articles.length : 0) as number;
          const done = (Array.isArray(st.analyzedArticles) ? st.analyzedArticles.length : 0) as number;
          if (total > 0 && done >= 0 && done < total) {
            // Defer to next tick to ensure state is applied
            setTimeout(() => {
              resumePendingAnalysis(done);
            }, 0);
          }
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const payload = {
        searchMode,
        searchSource,
        query,
        maxResults,
        minScore,
        useBoolean,
        articles,
        analyzedArticles,
        expandedCards: Array.from(expandedCards),
        selectedArticles: Array.from(selectedArticles),
        activityLog,
        progress,
        activeMainTab,
      };
      localStorage.setItem('literature_review_enhanced_state', JSON.stringify(payload));
    } catch {}
  }, [searchMode, searchSource, query, maxResults, minScore, useBoolean, articles, analyzedArticles, expandedCards, selectedArticles, activityLog, progress, activeMainTab]);

  // Resume remaining analysis from a given index
  const resumePendingAnalysis = useCallback(async (startIndex: number) => {
    if (!articles || articles.length === 0) return;
    try {
      setAnalyzing(true);
      for (let i = startIndex; i < articles.length; i++) {
        const article = articles[i];
        setCurrentStep(`Analyzing ${i + 1}/${articles.length}: ${article.title.substring(0, 50)}...`);
        setProgress(40 + (40 * (i + 1) / articles.length));
        const annotateResponse = await apiClient.post<any>(
          '/api/literature/annotate',
          {
            title: article.title,
            url: article.url,
            snippet: article.snippet,
            pdfUrl: article.pdf_url,
            userQuery: query
          }
        );

        const ai = annotateResponse.data;
        if (!ai) continue;
        const analyzedArticle = {
          ...article,
          analysis: ai,
          pharmacovigilanceData: ai.pharmacovigilanceData,
          status: 'done' as const
        };
        setAnalyzedArticles(prev => {
          // Avoid duplicates if resume is triggered multiple times
          const exists = prev.some(a => a.url === analyzedArticle.url);
          return exists ? prev : [...prev, analyzedArticle];
        });
        if (ai.pharmacovigilanceData?.confidence_score > 0.5) {
          try { await createCasesFromResults([analyzedArticle]); } catch {}
        }
      }
      setProgress(100);
      setCurrentStep('Complete');
      setSnackbar({ open: true, message: 'Analysis resumed and completed!', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: 'Resume failed', severity: 'error' });
    } finally {
      setAnalyzing(false);
      setCurrentStep('');
    }
  }, [articles, query]);

  // Search Handler
  const handleSearch = useCallback(async () => {
    if (!query.trim()) {
      setSnackbar({ open: true, message: 'Please enter a search query', severity: 'warning' });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setArticles([]);
    setAnalyzedArticles([]);
    setProgress(0);
    addLog('Starting search...', 'info');

    try {
      // Build Boolean Query
      let finalQuery = query;
      if (useBoolean && searchMode === 'keyword') {
        setCurrentStep('Building boolean query...');
        const booleanResponse = await apiClient.post<{ booleanQuery?: string }>(
          '/api/literature/boolean',
          { userPrompt: query }
        );
        if (booleanResponse.data?.booleanQuery) {
          finalQuery = booleanResponse.data.booleanQuery;
          addLog(`Boolean query: ${finalQuery}`, 'info');
        } else if (booleanResponse.error) {
          addLog('Boolean query failed, using original', 'warn');
        }
      }
      setProgress(20);

      // Search
      setCurrentStep('Searching articles...');
      const searchPayload = {
        mode: searchMode,
        query: searchMode === 'keyword' ? finalQuery : undefined,
        text: searchMode === 'paste' ? query : undefined,
        source: searchSource,
        maxResults,
        minScore
      };

      const searchResponse = await apiClient.post<{ results?: Article[] }>(
        '/api/literature/search',
        searchPayload,
        {
          signal: abortControllerRef.current.signal
        }
      );
      
      const results = searchResponse.data?.results || [];
      setArticles(results);
      addLog(`Found ${results.length} articles`, 'info');
      setProgress(40);

      // Analyze Articles
      setCurrentStep('Analyzing articles...');
      setAnalyzing(true);
      
      for (let i = 0; i < results.length; i++) {
        const article = results[i];
        setCurrentStep(`Analyzing ${i + 1}/${results.length}: ${article.title.substring(0, 50)}...`);
        setProgress(40 + (40 * (i + 1) / results.length));
        
        const annotateResponse = await apiClient.post<any>(
          '/api/literature/annotate',
          {
            title: article.title,
            url: article.url,
            snippet: article.snippet,
            pdfUrl: article.pdf_url,
            userQuery: query
          },
          { signal: abortControllerRef.current.signal }
        );

        const ai = annotateResponse.data;
        let analyzedArticle: Article;
        if (!ai) {
          addLog(`Analysis unavailable for: ${article.title.substring(0, 50)}... (showing basic result)`, 'warn');
          analyzedArticle = {
            ...article,
            analysis: {
              abstract: article.snippet || '',
              tags: [],
              score3: 0,
              // minimal shape expected by UI; no AE implied
            } as any,
            pharmacovigilanceData: undefined,
            status: 'done'
          } as Article;
        } else {
          analyzedArticle = {
            ...article,
            analysis: ai,
            pharmacovigilanceData: ai.pharmacovigilanceData,
            status: 'done' as const
          } as Article;
        }

        setAnalyzedArticles(prev => [...prev, analyzedArticle]);
        
        // Create case when AE detected by analysis OR minimal extracted fields exist.
        // Do not rely solely on PV confidence (may be 0 when quota is hit).
        const ef = (ai as any)?.extractedFields || {};
        const aeDetected = Boolean(
          (ai as any)?.adverse_event_analysis?.contains_adverse_events ||
          ef?.event_verbatim || ef?.suspect_product_name
        );
        if (aeDetected) {
          addLog(`Creating case for: ${article.title.substring(0, 50)}...`, 'info');
          try {
            await createCasesFromResults([analyzedArticle]);
            addLog('Case created', 'success');
          } catch (error) {
            addLog('Failed to create case', 'error');
          }
        }
      }

      setProgress(100);
      setCurrentStep('Complete');
      addLog('Search completed', 'success');
      setSnackbar({ open: true, message: 'Search completed!', severity: 'success' });

    } catch (error: any) {
      if (error.name === 'AbortError') {
        addLog('Search cancelled', 'warn');
      } else {
        addLog(`Search failed: ${error.message}`, 'error');
        setSnackbar({ open: true, message: 'Search failed', severity: 'error' });
      }
    } finally {
      setLoading(false);
      setAnalyzing(false);
      setCurrentStep('');
    }
  }, [query, searchMode, searchSource, maxResults, minScore, useBoolean, addLog]);

  // Cancel Search
  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      addLog('Search cancelled', 'warn');
    }
  }, [addLog]);

  // Toggle Card Expansion
  const toggleCardExpansion = useCallback((url: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(url)) {
        newSet.delete(url);
      } else {
        newSet.add(url);
      }
      return newSet;
    });
  }, []);

  // Export Selected
  const handleExport = useCallback(() => {
    const selected = analyzedArticles.filter(a => selectedArticles.has(a.url));
    if (selected.length === 0) {
      setSnackbar({ open: true, message: 'No articles selected', severity: 'warning' });
      return;
    }

    const exportData = selected.map(article => ({
      title: article.title,
      url: article.url,
      authors: article.authors_info,
      year: article.year,
      venue: article.venue,
      abstract: article.analysis?.abstract,
      aiScore: article.analysis?.score3,
      tags: article.analysis?.tags,
      pvData: article.pharmacovigilanceData
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `literature-review-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    addLog(`Exported ${selected.length} articles`, 'success');
  }, [analyzedArticles, selectedArticles, addLog]);

  // Render Article Card
  const renderArticleCard = (article: Article) => {
    const isExpanded = expandedCards.has(article.url);
    const isSelected = selectedArticles.has(article.url);
    const aiScore = article.analysis?.score3 || 0;
    // Determine AE presence using PV data or extractedFields/adverse_event_analysis
    const hasAE = Boolean(
      (article.pharmacovigilanceData?.confidence_score || 0) > 0 ||
      (article.analysis as any)?.adverse_event_analysis?.contains_adverse_events ||
      (article.analysis as any)?.extractedFields?.event_verbatim ||
      (article.analysis as any)?.extractedFields?.suspect_product_name
    );

    // uses mapExtractedToICSR from component scope

    return (
      <StyledCard key={article.url} sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h6" sx={{ flex: 1, pr: 2 }}>
              {article.title}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedArticles(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(article.url)) {
                    newSet.delete(article.url);
                  } else {
                    newSet.add(article.url);
                  }
                  return newSet;
                });
              }}
            >
              {isSelected ? <CheckCircleIcon color="primary" /> : <CheckIcon />}
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }} flexWrap="wrap">
            {article.year && <Chip size="small" label={article.year} />}
            {article.venue && <Chip size="small" label={article.venue} variant="outlined" />}
            {article.source && (
              <Chip 
                size="small" 
                label={article.source}
                color={article.source === 'pubmed' ? 'primary' : 'secondary'}
                sx={{ fontWeight: 600, borderRadius: 1 }}
              />
            )}
            {aiScore > 0 && (
              <Chip 
                size="small" 
                label={`AI: ${aiScore}/3`}
                color={aiScore === 3 ? 'success' : aiScore === 2 ? 'warning' : 'default'}
                sx={{ fontWeight: 600, borderRadius: 1 }}
              />
            )}
            {hasAE && (
              <Chip size="small" label="AE" color="error" icon={<WarningIcon />} />
            )}
          </Stack>

          {article.authors_info && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {article.authors_info}
            </Typography>
          )}

          <Collapse in={!isExpanded}>
            <Typography variant="body2">
              {article.snippet?.substring(0, 200)}...
            </Typography>
          </Collapse>

          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2 }}>
              {article.snippet && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Abstract:</Typography>
                  <Typography variant="body2">{article.snippet}</Typography>
                </Box>
              )}

              {article.analysis?.abstract && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>AI Analysis:</Typography>
                  <Typography variant="body2">{article.analysis.abstract}</Typography>
                </Box>
              )}

              {article.analysis?.tags && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Tags:</Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {article.analysis.tags.map((tag, i) => (
                      <Chip key={i} label={tag} size="small" sx={{ mb: 0.5 }} />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* ICSR Information Display */}
              <ICSRDisplay 
                data={mapExtractedToICSR((article.analysis as any)?.extractedFields)}
                pharmacovigilanceData={article.pharmacovigilanceData}
                compact={false}
              />
            </Box>
          </Collapse>
        </CardContent>

        <CardActions>
          <Button
            size="small"
            startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => toggleCardExpansion(article.url)}
          >
            {isExpanded ? 'Less' : 'More'}
          </Button>
          <Button size="small" startIcon={<LinkIcon />} href={article.url} target="_blank">
            Source
          </Button>
          {article.pdf_url && (
            <Button size="small" startIcon={<PdfIcon />} href={article.pdf_url} target="_blank">
              PDF
            </Button>
          )}
        </CardActions>
      </StyledCard>
    );
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            📚 Enhanced Literature Review
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Advanced search and analysis with improved UI/UX
          </Typography>
        </Paper>

        {/* In-module tabs: Search | Case Management */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={activeMainTab} onChange={(_, v) => setActiveMainTab(v)}>
            <Tab label="Search" />
            <Tab label="Case Management" />
          </Tabs>
        </Paper>

        {activeMainTab === 0 ? (
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* Search Panel */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 33%' } }}>
            <GlassCard sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" gutterBottom>Search Configuration</Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Mode</InputLabel>
                <Select value={searchMode} onChange={(e) => setSearchMode(e.target.value as any)} label="Mode">
                  <MenuItem value="keyword">Keyword</MenuItem>
                  <MenuItem value="paste">Paste</MenuItem>
                  <MenuItem value="lookup">URL/DOI</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Source</InputLabel>
                <Select value={searchSource} onChange={(e) => setSearchSource(e.target.value as any)} label="Source">
                  <MenuItem value="semantic_scholar">Semantic Scholar</MenuItem>
                  <MenuItem value="pubmed">PubMed</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ mb: 2 }}
              />

              {searchMode === 'keyword' && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Typography>Boolean Query</Typography>
                  <ToggleButton
                    value="check"
                    selected={useBoolean}
                    onChange={() => setUseBoolean(!useBoolean)}
                    size="small"
                  >
                    {useBoolean ? <CheckIcon /> : <CloseIcon />}
                  </ToggleButton>
                </Stack>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>Max Results: {maxResults}</Typography>
                <Slider value={maxResults} onChange={(_, v) => setMaxResults(v as number)} min={1} max={50} />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom>Min Score: {minScore}</Typography>
                <Slider value={minScore} onChange={(_, v) => setMinScore(v as number)} min={0} max={3} marks />
              </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading || !query.trim()}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
                {loading && (
                  <IconButton color="error" onClick={handleCancel}>
                    <StopIcon />
                  </IconButton>
                )}
              </Stack>

              {loading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="caption">{currentStep}</Typography>
                </Box>
              )}

              {/* Activity Log */}
              <Accordion sx={{ mt: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Activity Log</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {activityLog.map((log, i) => (
                      <ListItem key={i}>
                        <ListItemIcon>
                          {log.type === 'error' ? <ErrorIcon color="error" /> :
                           log.type === 'warn' ? <WarningIcon color="warning" /> :
                           log.type === 'success' ? <CheckCircleIcon color="success" /> :
                           <InfoIcon />}
                        </ListItemIcon>
                        <ListItemText primary={log.message} secondary={log.time} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </GlassCard>
          </Box>

          {/* Results and ICSR Details */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 66%' } }}>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: analyzedArticles.length > 0 ? '7fr 5fr' : '1fr' } }}>
              {/* Results Column */}
              <Box>
            {analyzedArticles.length > 0 && (
              <>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                      {analyzedArticles.length} Results
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleExport}
                        disabled={selectedArticles.size === 0}
                      >
                        Export ({selectedArticles.size})
                      </Button>
                      <Button
                        size="small"
                        startIcon={<FilterIcon />}
                        onClick={() => setShowFiltered(!showFiltered)}
                      >
                        Filter
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>

                <Box>
                  {analyzedArticles.map(renderArticleCard)}
                </Box>
              </>
            )}

            {loading && articles.length === 0 && (
              <Box>
                {[1, 2, 3].map(i => (
                  <Card key={i} sx={{ mb: 2, p: 2 }}>
                    <Skeleton variant="text" width="80%" height={32} />
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="rectangular" height={100} sx={{ mt: 2 }} />
                  </Card>
                ))}
              </Box>
            )}
              </Box>

              {/* ICSR Summary Panel - Right Side */}
              {analyzedArticles.length > 0 && (
                <Box>
                  <Paper sx={{ p: 2, position: 'sticky', top: 20, maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
                    <Typography variant="h6" gutterBottom>
                      ICSR Summary
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Statistics */}
                    <Stack spacing={2} sx={{ mb: 3 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Total Articles:</Typography>
                        <Chip label={analyzedArticles.length} size="small" />
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">With Adverse Events:</Typography>
                        <Chip
                          label={analyzedArticles.filter(a => {
                            const ef = (a.analysis as any)?.extractedFields;
                            const hasAE = Boolean(
                              (a.pharmacovigilanceData?.confidence_score || 0) > 0 ||
                              (a.analysis as any)?.adverse_event_analysis?.contains_adverse_events ||
                              ef?.event_verbatim || ef?.suspect_product_name
                            );
                            return hasAE;
                          }).length}
                          size="small"
                          color="error"
                        />
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Cases Created:</Typography>
                        <Chip
                          label={analyzedArticles.filter(a => {
                            const ef = (a.analysis as any)?.extractedFields;
                            return Boolean(
                              (a.pharmacovigilanceData?.confidence_score || 0) > 0.5 ||
                              ef?.event_verbatim || ef?.suspect_product_name
                            );
                          }).length}
                          size="small"
                          color="success"
                        />
                      </Stack>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Selected Article ICSR Details */}
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>
                          Selected Article ICSR Details
                        </Typography>
                        {analyzedArticles
                          .filter(a => selectedArticles.has(a.url))
                          .slice(0, 1)
                          .map(article => (
                            <Box key={article.url}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {article.title.substring(0, 100)}...
                              </Typography>
                              <ICSRDisplay
                                data={mapExtractedToICSR((article.analysis as any)?.extractedFields)}
                                pharmacovigilanceData={article.pharmacovigilanceData}
                                compact={true}
                              />
                            </Box>
                          ))
                        }
                      </Box>

                    {/* Quick Actions */}
                    <Box sx={{ mt: 3 }}>
                      <Stack spacing={1}>
                        <Button 
                          fullWidth 
                          variant="outlined" 
                          onClick={() => setActiveMainTab(1)}
                        >
                          Open Case Management
                        </Button>
                        <Button 
                          fullWidth 
                          variant="contained" 
                          disabled={selectedArticles.size === 0}
                          onClick={handleExport}
                        >
                          Export Selected ({selectedArticles.size})
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        ) : (
          <Box>
            <CaseManagement />
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default LiteratureReviewEnhanced;
